import axios from "axios";
import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import EditPricingModal from "./EditPricingModal";
import EditPaygPricingModal from "./EditPaygPricingModal";
import { DEFAULT_PAYG_PLAN } from "../../../constants";
import './PricingConfig.css';

const PricingConfig = () => {
  const [pricingConfig, setPricingConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editPaygModal, setEditPaygModal] = useState(false);
  // Which price sheet is on screen. Seat and PAYG are two independent price lists that
  // happen to share one config document, so they get one tab each.
  const [activeTab, setActiveTab] = useState('seat');
  const [changed, setChanged] = useState(false);

  // Dynamic calculator state
  const [userCounts, setUserCounts] = useState({
    companyUser: 1,
    readWrite: 0,
    topo: 0,
    readOnly: 0
  });

  const [addOnCounts, setAddOnCounts] = useState({
    foundationAnalysisAddOn: 0,
    floorLevelAddOn: 0
  });

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    getPricingConfig();
    var element = document.getElementById("page-container");
    element.classList.add("show");
  }, []);

  const getPricingConfig = async () => {
    try {
      setIsLoading(true);
      const myurl = `${process.env.REACT_APP_base_url}api/system-config/default-pricing`;

      const response = await axios.get(myurl, {
        headers: {
          auth_code: process.env.REACT_APP_AUTH_CODE
        }
      });

      if (response.data.success === true) {
        setPricingConfig(response.data.data);
        setIsLoading(false);
      } else {
        toast.error(response.data.message || "Failed to load pricing configuration");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching pricing config:", error);
      toast.error("Failed to load pricing configuration");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (changed) {
      getPricingConfig();
      setChanged(false);
    }
  }, [changed]);

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const updateUserCount = (userType, delta) => {
    setUserCounts(prev => ({
      ...prev,
      [userType]: Math.max(0, prev[userType] + delta)
    }));
  };

  const setUserCount = (userType, count) => {
    const numCount = parseInt(count) || 0;
    setUserCounts(prev => ({
      ...prev,
      [userType]: Math.max(0, numCount)
    }));
  };

  const updateAddOnCount = (addOnType, delta) => {
    const totalUsers = getTotalUsers();
    setAddOnCounts(prev => ({
      ...prev,
      [addOnType]: Math.max(0, Math.min(totalUsers, prev[addOnType] + delta))
    }));
  };

  const setAddOnCount = (addOnType, count) => {
    const totalUsers = getTotalUsers();
    const numCount = parseInt(count) || 0;
    setAddOnCounts(prev => ({
      ...prev,
      [addOnType]: Math.max(0, Math.min(totalUsers, numCount))
    }));
  };

  // Auto-adjust add-on counts when total users decrease
  useEffect(() => {
    const totalUsers = getTotalUsers();
    setAddOnCounts(prev => ({
      foundationAnalysisAddOn: Math.min(prev.foundationAnalysisAddOn, totalUsers),
      floorLevelAddOn: Math.min(prev.floorLevelAddOn, totalUsers)
    }));
  }, [userCounts]);

  const calculateTotalCost = () => {
    if (!pricingConfig) return 0;

    let total = pricingConfig.baseCharge;

    // Add user costs
    total += userCounts.companyUser * pricingConfig.companyUser;
    total += userCounts.readWrite * pricingConfig.readWrite;
    total += userCounts.topo * pricingConfig.topo;
    total += userCounts.readOnly * pricingConfig.readOnly;

    // Add add-on costs
    if (addOnCounts.foundationAnalysisAddOn > 0) {
      total += addOnCounts.foundationAnalysisAddOn * (pricingConfig.foundationAnalysisAddOn || 0);
    }
    if (addOnCounts.floorLevelAddOn > 0) {
      total += addOnCounts.floorLevelAddOn * (pricingConfig.floorLevelAddOn || 0);
    }

    return total;
  };

  const getTotalUsers = () => {
    return Object.values(userCounts).reduce((sum, count) => sum + count, 0);
  };

  const PricingCard = ({ title, items, icon, className = "", intro, note }) => (
    <div className={`pricing-card ${className}`}>
      <div className="pricing-card-header">
        <div className="pricing-card-title">
          <i className={`fa ${icon} pricing-card-icon mr-2`}></i>
          {title}
        </div>
      </div>
      <div className="pricing-card-body">
        {intro && <p className="text-muted mb-2">{intro}</p>}
        <table className="pricing-table">
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className={item.emphasis ? 'pricing-total-row' : undefined}>
                <td className="pricing-label">{item.label}</td>
                <td className="pricing-value">
                  {item.type === 'currency' ? (
                    <span className="pricing-currency-badge">
                      {item.value}
                    </span>
                  ) : item.type === 'text' ? (
                    <span className="pricing-text">
                      {item.value}
                    </span>
                  ) : (
                    <span className="pricing-amount">
                      {item.value}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {note && <small className="text-muted d-block mt-2">{note}</small>}
      </div>
    </div>
  );

  const renderPricingCards = () => {
    if (!pricingConfig) return null;

    const basePricingItems = [
      {
        label: "Base Monthly Charge",
        value: formatCurrency(pricingConfig.baseCharge, pricingConfig.currency),
        type: "amount"
      },
      {
        label: "Currency",
        value: pricingConfig.currency?.toUpperCase() || 'USD',
        type: "currency"
      }
    ];

    const userTypePricingItems = [
      {
        label: "Company User",
        value: formatCurrency(pricingConfig.companyUser, pricingConfig.currency),
        type: "amount"
      },
      {
        label: "Read Write User",
        value: formatCurrency(pricingConfig.readWrite, pricingConfig.currency),
        type: "amount"
      },
      {
        label: "Topo User (seat)",
        value: formatCurrency(pricingConfig.topo, pricingConfig.currency),
        type: "amount"
      },
      {
        label: "Read Only User",
        value: formatCurrency(pricingConfig.readOnly, pricingConfig.currency),
        type: "amount"
      }
    ];

    const addOnPricingItems = [
      {
        label: "Foundation Analysis Add-On",
        value: formatCurrency(pricingConfig.foundationAnalysisAddOn || 0, pricingConfig.currency),
        type: "amount"
      },
      {
        label: "Floor Level Add-On",
        value: formatCurrency(pricingConfig.floorLevelAddOn || 0, pricingConfig.currency),
        type: "amount"
      }
    ];

    return (
      <div className="pricing-cards-grid">
        <PricingCard
          title="Base Pricing"
          items={basePricingItems}
          icon="fa-building"
        />
        <PricingCard
          title="User Type Pricing"
          items={userTypePricingItems}
          icon="fa-users"
        />
        <PricingCard
          title="Add-On Features"
          items={addOnPricingItems}
          icon="fa-plus-circle"
        />
      </div>
    );
  };

  const renderDynamicCalculator = () => {
    if (!pricingConfig) return null;

    const userTypes = [
      {
        key: 'companyUser',
        name: 'Company User',
        price: pricingConfig.companyUser,
        icon: 'fa-user-tie',
        description: 'Admin access with full permissions'
      },
      {
        key: 'readWrite',
        name: 'Read Write User',
        price: pricingConfig.readWrite,
        icon: 'fa-edit',
        description: 'Can view and edit content'
      },
      {
        key: 'topo',
        name: 'Topo User (seat)',
        price: pricingConfig.topo,
        icon: 'fa-map',
        description: 'Specialized topology access'
      },
      {
        key: 'readOnly',
        name: 'Read Only User',
        price: pricingConfig.readOnly,
        icon: 'fa-eye',
        description: 'View-only access'
      }
    ];

    const addOnFeatures = [
      {
        key: 'foundationAnalysisAddOn',
        name: 'Foundation Analysis',
        price: pricingConfig.foundationAnalysisAddOn || 0,
        icon: 'fa-hammer',
        description: 'Advanced foundation analysis tools (per user)'
      },
      {
        key: 'floorLevelAddOn',
        name: 'Floor Level Analysis',
        price: pricingConfig.floorLevelAddOn || 0,
        icon: 'fa-layer-group',
        description: 'Multi-floor level analysis (per user)'
      }
    ];

    return (
      <div className="cost-calculator-card">
        <div className="cost-calculator-header">
          <div className="cost-calculator-title">
            <i className="fa fa-calculator"></i>
            Dynamic Cost Calculator
          </div>
        </div>
        <div className="cost-calculator-body">
          <div className="row">
            <div className="col-lg-8">
              <div className="calculator-controls">
                {/* User Types Section */}
                <div className="calculator-section">
                  <div className="calculator-section-title">
                    <i className="fa fa-users"></i>
                    User Types
                  </div>
                  {userTypes.map(userType => (
                    <div key={userType.key} className="user-type-control">
                      <div className="user-type-info">
                        <div className="user-type-name">
                          <i className={`fa ${userType.icon} me-2 mr-2`}></i>
                          {userType.name}
                        </div>
                        <div className="user-type-price">
                          {formatCurrency(userType.price, pricingConfig.currency)} per user/month
                        </div>
                        <small className="text-muted">{userType.description}</small>
                      </div>
                      <div className="user-count-control">
                        <button
                          type="button"
                          className="count-btn"
                          onClick={() => updateUserCount(userType.key, -1)}
                          disabled={userCounts[userType.key] === 0 || (userType.key === 'companyUser' && userCounts[userType.key] === 1)}
                        >
                          <i className="fa fa-minus"></i>
                        </button>
                        <input
                          type="number"
                          className="count-input"
                          value={userCounts[userType.key]}
                          onChange={(e) => setUserCount(userType.key, e.target.value)}
                          min={userType.key === 'companyUser' ? 1 : 0}
                        />
                        <button
                          type="button"
                          className="count-btn"
                          onClick={() => updateUserCount(userType.key, 1)}
                          disabled={userCounts[userType.key] >= 99}
                        >
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add-On Features Section */}
                <div className="calculator-section">
                  <div className="calculator-section-title">
                    <i className="fa fa-plus-circle"></i>
                    Add-On Features (Per User)
                  </div>
                  {getTotalUsers() === 0 ? (
                    <div className="alert alert-info">
                      <i className="fa fa-info-circle me-2"></i>
                      Add users first to enable add-on features
                    </div>
                  ) : (
                    addOnFeatures.map(addOn => (
                      <div key={addOn.key} className="user-type-control">
                        <div className="user-type-info">
                          <div className="user-type-name">
                            <i className={`fa ${addOn.icon} me-2  mr-2`}></i>
                            {addOn.name}
                          </div>
                          <div className="user-type-price">
                            {formatCurrency(addOn.price, pricingConfig.currency)} per user/month
                          </div>
                          <small className="text-muted">
                            {addOn.description}
                            <span className="ml-2 ms-2 badge bg-secondary">
                              Max: {getTotalUsers()} users
                            </span>
                          </small>
                        </div>
                        <div className="user-count-control">
                          <button
                            type="button"
                            className="count-btn"
                            onClick={() => updateAddOnCount(addOn.key, -1)}
                            disabled={addOnCounts[addOn.key] === 0}
                          >
                            <i className="fa fa-minus"></i>
                          </button>
                          <input
                            type="number"
                            className="count-input"
                            value={addOnCounts[addOn.key]}
                            onChange={(e) => setAddOnCount(addOn.key, e.target.value)}
                            min={0}
                            max={getTotalUsers()}
                          />
                          <button
                            type="button"
                            className="count-btn"
                            onClick={() => updateAddOnCount(addOn.key, 1)}
                            disabled={addOnCounts[addOn.key] >= getTotalUsers()}
                          >
                            <i className="fa fa-plus"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="cost-summary">
                <h6 className="mb-3">
                  <i className="fa fa-receipt me-2 mr-2"></i>
                  Cost Breakdown
                </h6>
                <div className="cost-breakdown">
                  {/* Base Charge */}
                  <div className="cost-item">
                    <span className="cost-item-label">
                      <i className="fa fa-building me-2"></i>
                      Base Charge
                    </span>
                    <span className="cost-item-value">
                      {formatCurrency(pricingConfig.baseCharge, pricingConfig.currency)}
                    </span>
                  </div>

                  {/* User Costs */}
                  {userTypes.map(userType => {
                    if (userCounts[userType.key] === 0) return null;
                    return (
                      <div key={userType.key} className="cost-item">
                        <span className="cost-item-label">
                          <i className={`fa ${userType.icon} me-2`}></i>
                          {userType.name}
                          <span className="cost-item-count">
                            {userCounts[userType.key]}x
                          </span>
                        </span>
                        <span className="cost-item-value">
                          {formatCurrency(userType.price * userCounts[userType.key], pricingConfig.currency)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Add-On Costs */}
                  {addOnFeatures.map(addOn => {
                    if (addOnCounts[addOn.key] === 0) return null;
                    return (
                      <div key={addOn.key} className="cost-item">
                        <span className="cost-item-label">
                          <i className={`fa ${addOn.icon} me-2`}></i>
                          {addOn.name}
                          <span className="cost-item-count">
                            {addOnCounts[addOn.key]}x
                          </span>
                        </span>
                        <span className="cost-item-value">
                          {formatCurrency(addOn.price * addOnCounts[addOn.key], pricingConfig.currency)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Total */}
                  <div className="cost-item">
                    <span className="cost-item-label">
                      <strong>
                        <i className="fa fa-calculator me-2 mr-2"></i>
                        Total Monthly Cost
                      </strong>
                    </span>
                    <span className="cost-item-value cost-total">
                      {formatCurrency(calculateTotalCost(), pricingConfig.currency)}
                    </span>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Total Users:</small>
                    <strong>{getTotalUsers()}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">Active Add-ons:</small>
                    <strong>{addOnCounts.foundationAnalysisAddOn + addOnCounts.floorLevelAddOn} users</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaygPricing = () => {
    const payg = { ...DEFAULT_PAYG_PLAN, ...(pricingConfig?.payg || {}) };
    const money = (amount) => formatCurrency(amount || 0, payg.currency);
    const exampleTopos = 20;
    const exampleUsage = (Number(payg.pricePerTopo) || 0) * exampleTopos;
    const exampleTotal = exampleUsage + (Number(payg.baseCharge) || 0);

    return (
      <div className="pricing-cards-grid">
        <PricingCard
          title="Pay As You Go"
          icon="fa-bolt"
          items={[
            { label: "Base Price", value: `${money(payg.baseCharge)} /month`, type: "amount" },
            { label: "Per Topo Charge", value: `${money(payg.pricePerTopo)} /topo`, type: "amount" },
            { label: "Invoice Day", value: `${payg.invoiceDayOfMonth} of the following month`, type: "text" },
            { label: "Currency", value: (payg.currency || 'usd').toUpperCase(), type: "currency" },
          ]}
        />
        <PricingCard
          title="Not Charged"
          icon="fa-check-circle"
          items={[
            { label: "Users", value: "Unlimited, any type", type: "text" },
            { label: "Foundation Analysis", value: "Included", type: "text" },
            { label: "Floor Level", value: "Included", type: "text" },
            { label: "Storage & analysis runs", value: "Included", type: "text" },
          ]}
        />
        <PricingCard
          title="Example"
          icon="fa-calculator"
          intro={
            <>
              A company creating <strong>{exampleTopos} topos in August</strong>, invoiced on
              the {payg.invoiceDayOfMonth} of September:
            </>
          }
          items={[
            ...(Number(payg.baseCharge) > 0
              ? [{ label: "Monthly base — September", value: money(payg.baseCharge), type: "amount" }]
              : []),
            {
              label: `Topo usage — August (${exampleTopos} × ${money(payg.pricePerTopo)})`,
              value: money(exampleUsage),
              type: "amount",
            },
            { label: "Total", value: money(exampleTotal), type: "amount", emphasis: true },
          ]}
          note={<>The base fee covers the month <em>ahead</em>; usage covers the month just closed.</>}
        />
      </div>
    );
  };

  return (
    <Fragment>
      <Loader />
      <div id="page-container" className="fade page-sidebar-fixed page-header-fixed">
        <Menu />
        <EditPricingModal
          activeModal={editModal}
          setActiveModal={() => setEditModal(false)}
          setUpdateModal={() => setChanged(true)}
          currentConfig={pricingConfig}
        />
        <EditPaygPricingModal
          activeModal={editPaygModal}
          setActiveModal={() => setEditPaygModal(false)}
          setUpdateModal={() => setChanged(true)}
          currentConfig={pricingConfig}
        />

        <div id="content" className="content pricing-config-page">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Pricing Configuration</li>
          </ol>
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h1 className="page-header mb-1">Pricing Configuration</h1>
              <p className="text-muted mb-3">
                Manage default pricing structure for new companies and subscription plans
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                setChanged(false);
                // Edit whichever price sheet is on screen - two separate forms, because
                // a per-seat price and a per-topo price share almost no fields.
                if (activeTab === 'payg') setEditPaygModal(true);
                else setEditModal(true);
              }}
              title="Edit Pricing Configuration"
            >
              <i className="fa fa-edit me-2 mr-2"></i>
              {activeTab === 'payg' ? 'Edit Pay As You Go Pricing' : 'Edit Seat Pricing'}
            </button>
          </div>

          <div className="alert alert-info">
            <i className="fa fa-info-circle me-2 mr-2"></i>
            <strong>Important Information</strong>
            <div className="mt-1">
              {activeTab === 'payg' ? (
                <>
                  This is the default Pay As You Go price list for new companies on that billing
                  model. Companies are charged per topo created plus a monthly base charge, and
                  invoiced monthly; there are no seats or add-ons to price. Changes made here will
                  only affect newly created companies and will not modify existing company pricing
                  plans.
                </>
              ) : (
                <>
                  This pricing configuration serves as the default template for all new companies.
                  Changes made here will only affect newly created companies and will not modify
                  existing company pricing plans.
                </>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner">
                <i className="fa fa-spinner fa-spin"></i>
              </div>
              <p className="loading-text">Loading pricing configuration...</p>
            </div>
          ) : pricingConfig ? (
            <div className="card">
              <div className="card-header">
                {/* Same tab markup as Companies / Users so the tabs look identical app-wide. */}
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'seat' ? 'active' : ''}`}
                      onClick={() => setActiveTab('seat')}
                      style={{
                        border: "none",
                        background: "none",
                        color: activeTab === 'seat' ? "#007bff" : "#6c757d"
                      }}
                    >
                      <i className="fa fa-users me-1 mr-2"></i>
                      Seat-based
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'payg' ? 'active' : ''}`}
                      onClick={() => setActiveTab('payg')}
                      style={{
                        border: "none",
                        background: "none",
                        color: activeTab === 'payg' ? "#007bff" : "#6c757d"
                      }}
                    >
                      <i className="fa fa-bolt me-1 mr-2"></i>
                      Pay As You Go
                    </button>
                  </li>
                </ul>
              </div>

              <div className="card-body">
                <h5 className="pricing-plan-heading">
                  Current Default Pricing Plan
                  <span className="badge bg-success ms-2 ml-2">Active</span>
                </h5>
                {activeTab === 'seat' ? (
                  <>
                    {renderPricingCards()}
                    {renderDynamicCalculator()}
                  </>
                ) : (
                  renderPaygPricing()
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <h4 className="empty-state-title">No Pricing Configuration Found</h4>
              <p className="empty-state-text">
                No default pricing configuration has been set up yet.
                Click the button below to create your first pricing plan.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setEditModal(true)}
              >
                <i className="fa fa-plus me-2 mr-2"></i>
                Set Up Pricing Configuration
              </button>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </Fragment>
  );
};

export default PricingConfig;